import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Toolbar,
  Typography,
} from '@mui/material';
import { Check as CheckIcon, Close as CloseIcon, Login as LoginIcon } from '@mui/icons-material';
import EncycdataLogo from '@components/layout/EncycdataLogo';

const pricingComparisonRows: Array<{
  feature: string;
  basic: boolean;
  advanced: boolean;
  coach: boolean;
  academy: boolean;
}> = [
  { feature: 'Basic skill test access', basic: true, advanced: true, coach: true, academy: true },
  { feature: 'Advanced skill set access', basic: false, advanced: true, coach: true, academy: true },
  { feature: 'Skill / stroke / drill preview videos', basic: true, advanced: true, coach: true, academy: true },
  { feature: 'Complete skill library after login', basic: false, advanced: true, coach: true, academy: true },
  { feature: 'Training homework workflow', basic: false, advanced: false, coach: true, academy: true },
  { feature: 'Coach dashboard and player tracking', basic: false, advanced: false, coach: true, academy: true },
  { feature: 'Academy multi-coach / multi-player management', basic: false, advanced: false, coach: false, academy: true },
  { feature: 'Priority support and onboarding', basic: false, advanced: false, coach: true, academy: true },
];

const renderAvailability = (value: boolean) =>
  value ? <CheckIcon sx={{ color: '#16a34a', fontSize: 20 }} /> : <CloseIcon sx={{ color: '#d1d5db', fontSize: 20 }} />;

const PricingPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box>
      <AppBar position="sticky" sx={{ bgcolor: '#ffffff', boxShadow: 1 }}>
        <Toolbar>
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
            <EncycdataLogo height={20} />
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#1f2937', cursor: 'pointer' }} onClick={() => navigate('/')}>
              EncycGames
            </Typography>
          </Box>
          <Button variant="outlined" onClick={() => navigate('/')} sx={{ mr: 1 }}>
            Home
          </Button>
          <Button variant="contained" startIcon={<LoginIcon />} onClick={() => navigate('/login')} sx={{ bgcolor: '#1976d2' }}>
            Sign in
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Card sx={{ border: '1px solid #e5e7eb' }}>
          <CardContent>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f4c81', mb: 1 }}>
              Pricing Plans
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Compare subscriptions feature-wise. Coach and Academy / School plans are customized after discussion.
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 800 }}>Feature</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 800 }}>Basic<br />INR 500</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 800 }}>Advanced<br />INR 1000</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 800 }}>Coach<br />Custom</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 800 }}>Academy / School<br />Custom</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pricingComparisonRows.map((row) => (
                    <TableRow key={row.feature}>
                      <TableCell>{row.feature}</TableCell>
                      <TableCell align="center">{renderAvailability(row.basic)}</TableCell>
                      <TableCell align="center">{renderAvailability(row.advanced)}</TableCell>
                      <TableCell align="center">{renderAvailability(row.coach)}</TableCell>
                      <TableCell align="center">{renderAvailability(row.academy)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
              For Coach and Academy / School pricing, contact us for a customized plan.
            </Typography>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default PricingPage;
